import React, { useState } from "react";
import { Download, Check, AlertCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * LeadCaptureForm. Formulaire de génération de prospects qualifiés.
 * Poste vers /api/leads (FastAPI + MongoDB). Sur succès, propose le PDF.
 */
export default function LeadCaptureForm({
 pdfUrl,
 pdfLabel,
 interest = "opportunites-guinee-bissau",
 source = "opportunites-page",
}) {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

 const API_URL = process.env.REACT_APP_BACKEND_URL;

 const [form, setForm] = useState({
 first_name: "",
 last_name: "",
 company: "",
 country: "",
 email: "",
 });
 const [status, setStatus] = useState("idle"); // idle | submitting | success | error
 const [errorMsg, setErrorMsg] = useState("");

 const onChange = (e) => setForm((f) => ({...f, [e.target.name]: e.target.value }));

 const onSubmit = async (e) => {
 e.preventDefault;
 if (status === "submitting") return;
 setStatus("submitting");
 setErrorMsg("");
 try {
 const res = await fetch(`${API_URL}/api/leads`, {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({...form, interest, source, locale }),
 });
 if (!res.ok) {
 const err = await res.json.catch(() => ({}));
 throw new Error(err.detail || `HTTP ${res.status}`);
 }
 setStatus("success");
 } catch (err) {
 setStatus("error");
 setErrorMsg(
 l(
 "Une erreur est survenue. Merci de réessayer ou de nous écrire à contact@nanda-sa.com.",
 "An error occurred. Please try again or write to contact@nanda-sa.com.",
 "Ocorreu um erro. Tente novamente ou escreva para contact@nanda-sa.com."
 )
 );
 }
 };

 if (status === "success") {
 return (
 <div
 data-testid="lead-form-success"
 className="border border-nanda-terracotta/40 bg-nanda-sand/40 p-8 md:p-10"
 >
 <div className="flex items-start gap-4">
 <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-nanda-terracotta text-white shrink-0">
 <Check size={20} strokeWidth={2} />
 </div>
 <div className="flex-1">
 <div className="label-overline text-nanda-terracotta mb-2">
 {l("Merci", "Thank you", "Obrigado")}
 </div>
 <h3 className="font-serif text-2xl md:text-3xl text-nanda-ink leading-tight mb-4">
 {l(
 "Votre dossier est prêt.",
 "Your dossier is ready.",
 "O seu dossiê está pronto."
 )}
 </h3>
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed mb-6 max-w-xl">
 {l(
 "Nous vous remercions pour votre intérêt. Vous pouvez télécharger le dossier ci-dessous. Notre équipe restera à votre disposition pour toute question.",
 "Thank you for your interest. You can download the dossier below. Our team remains available for any question.",
 "Agradecemos o seu interesse. Pode transferir o dossiê abaixo. A nossa equipa fica à sua disposição para qualquer questão."
 )}
 </p>
 <a
 href={pdfUrl}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="lead-form-download"
 className="inline-flex items-center gap-3 bg-nanda-terracotta text-white px-7 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 <Download size={16} strokeWidth={1.5} />
 {pdfLabel || l("Télécharger le dossier PDF", "Download the PDF dossier", "Transferir o dossiê PDF")}
 </a>
 </div>
 </div>
 </div>
 );
 }

 return (
 <form
 onSubmit={onSubmit}
 data-testid="lead-form"
 className="border border-nanda-line bg-nanda-bg p-8 md:p-10"
 >
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <Field
 id="first_name"
 name="first_name"
 label={l("Prénom", "First name", "Nome próprio")}
 value={form.first_name}
 onChange={onChange}
 required
 />
 <Field
 id="last_name"
 name="last_name"
 label={l("Nom", "Last name", "Apelido")}
 value={form.last_name}
 onChange={onChange}
 required
 />
 <Field
 id="company"
 name="company"
 label={l("Société (optionnel)", "Company (optional)", "Sociedade (opcional)")}
 value={form.company}
 onChange={onChange}
 />
 <Field
 id="country"
 name="country"
 label={l("Pays", "Country", "País")}
 value={form.country}
 onChange={onChange}
 required
 />
 <div className="md:col-span-2">
 <Field
 id="email"
 name="email"
 type="email"
 label={l("Adresse e-mail", "Email address", "Endereço de e-mail")}
 value={form.email}
 onChange={onChange}
 required
 />
 </div>
 </div>
 <p className="mt-6 text-nanda-muted text-xs leading-relaxed max-w-xl">
 {l(
 "En soumettant ce formulaire, vous acceptez que NANDA SÁ conserve vos coordonnées pour vous transmettre le dossier demandé et, occasionnellement, des ressources complémentaires. Vous pouvez vous désinscrire à tout moment en écrivant à contact@nanda-sa.com.",
 "By submitting this form, you agree that NANDA SÁ may keep your details to send you the requested dossier and, occasionally, complementary resources. You can unsubscribe at any time by writing to contact@nanda-sa.com.",
 "Ao submeter este formulário, aceita que a NANDA SÁ conserve os seus dados para lhe enviar o dossiê solicitado e, ocasionalmente, recursos complementares. Pode cancelar a subscrição a qualquer momento escrevendo para contact@nanda-sa.com."
 )}
 </p>
 {status === "error" && (
 <div
 data-testid="lead-form-error"
 className="mt-6 flex items-start gap-3 border border-nanda-terracotta/50 bg-nanda-sand/40 p-4 text-sm text-nanda-ink"
 >
 <AlertCircle size={18} strokeWidth={1.5} className="text-nanda-terracotta mt-0.5 shrink-0" />
 <span>{errorMsg}</span>
 </div>
 )}
 <div className="mt-8">
 <button
 type="submit"
 disabled={status === "submitting"}
 data-testid="lead-form-submit"
 className="inline-flex items-center gap-3 bg-nanda-terracotta text-white px-8 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline disabled:opacity-50"
 >
 <Download size={16} strokeWidth={1.5} />
 {status === "submitting"
 ? l("Envoi…", "Sending…", "A enviar…")
 : l(
 "Recevoir le dossier gratuitement",
 "Receive the dossier for free",
 "Receber o dossiê gratuitamente"
 )}
 </button>
 </div>
 </form>
 );
}

const Field = ({ id, name, label, value, onChange, type = "text", required = false }) => (
 <div>
 <label htmlFor={id} className="label-overline text-nanda-muted block mb-3">
 {label}
 {required && <span className="text-nanda-terracotta ml-1">*</span>}
 </label>
 <input
 id={id}
 name={name}
 type={type}
 value={value}
 onChange={onChange}
 required={required}
 autoComplete={
 type === "email" ? "email" : name === "first_name" ? "given-name" : name === "last_name" ? "family-name" : name === "country" ? "country-name" : name === "company" ? "organization" : "off"
 }
 data-testid={`lead-field-${name}`}
 className="w-full border-b border-nanda-line bg-transparent py-3 text-nanda-ink focus:outline-none focus:border-nanda-ink transition-colors"
 />
 </div>
);
